/**
 * Dashboard utility functions for date calculations and compliance status
 */

/**
 * Calculate expiration date (5 years from registration date)
 */
export function calculateExpirationDate(registrationDate: string): Date {
    const regDate = new Date(registrationDate);
    const expirationDate = new Date(regDate);
    expirationDate.setFullYear(regDate.getFullYear() + 5);
    return expirationDate;
}

/**
 * Parse Assessment Year strings like "2022-23" or "2022-2023"
 */
export function parseAssessmentYear(assessmentYear: string): {
    startYear: number;
    endYear: number;
} | null {
    if (!assessmentYear) return null;
    const ay = assessmentYear.trim();
    const match = ay.match(/^(\d{4})\s*[-/]\s*(\d{2}|\d{4})$/);
    if (!match) return null;
    const startYear = parseInt(match[1], 10);
    const second = match[2];
    let endYear: number;
    if (second.length === 2) {
        const twoDigit = parseInt(second, 10);
        const centuryBase = startYear - (startYear % 100);
        endYear = centuryBase + twoDigit;
        // If wrap-around happens (e.g., 1999-00), ensure endYear >= startYear
        if (endYear < startYear) endYear += 100;
    } else {
        endYear = parseInt(second, 10);
    }
    return { startYear, endYear };
}

/**
 * Calculate expiry from Assessment Year: 31 Mar of (AY end year + 3)
 * Example: AY 2022-23 => 31 Mar 2026
 */
export function calculateExpiryFromAssessmentYear(assessmentYear: string): Date | null {
    const parsed = parseAssessmentYear(assessmentYear);
    if (!parsed) return null;
    const expiryYear = parsed.endYear + 3;
    return new Date(expiryYear, 2, 31); // Month is 0-based; 2 => March
}

/**
 * Calculate time remaining until expiration
 */
export function calculateTimeRemaining(expirationDate: Date): {
    months: number;
    days: number;
    isExpired: boolean;
} {
    const now = new Date();
    const diffTime = expirationDate.getTime() - now.getTime();

    if (diffTime <= 0) {
        return { months: 0, days: 0, isExpired: true };
    }

    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;

    return { months, days, isExpired: false };
}

/**
 * Format expiration date display
 */
export function formatExpirationDate(expirationDate: Date): string {
    return expirationDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
}

/**
 * Get compliance status styling based on time remaining
 */
export function getComplianceStatus(timeRemaining: {
    months: number;
    days: number;
    isExpired: boolean;
}): {
    className: string;
    text: string;
    bgColor: string;
    textColor: string;
} {
    const { months, days, isExpired } = timeRemaining;

    if (isExpired) {
        return {
            className: "bg-red-100 text-red-800",
            text: "Expired",
            bgColor: "bg-red-100",
            textColor: "text-red-800"
        };
    }

    // More than 12 months = green, less than 12 months = red
    if (months >= 12) {
        return {
            className: "bg-green-100 text-green-800",
            text: `Expires: ${formatTimeRemaining(months, days)}`,
            bgColor: "bg-green-100",
            textColor: "text-green-800"
        };
    }

    return {
        className: "bg-red-100 text-red-800",
        text: `Expires: ${formatTimeRemaining(months, days)}`,
        bgColor: "bg-red-100",
        textColor: "text-red-800"
    };
}

/**
 * Format time remaining for display
 */
export function formatTimeRemaining(months: number, days: number): string {
    if (months > 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
    }
    return `${days} day${days > 1 ? 's' : ''}`;
}

/**
 * Get full expiration display text with date and time remaining
 */
export function getExpirationDisplay(registrationDate: string): {
    expirationDate: string;
    timeRemaining: string;
    status: ReturnType<typeof getComplianceStatus>;
} {
    const expDate = calculateExpirationDate(registrationDate);
    const timeRem = calculateTimeRemaining(expDate);
    const status = getComplianceStatus(timeRem);

    return {
        expirationDate: formatExpirationDate(expDate),
        timeRemaining: formatTimeRemaining(timeRem.months, timeRem.days),
        status
    };
}


/**
 * Get urgency level for compliance
 */
export function getUrgencyLevel(registrationDate: string): 'low' | 'medium' | 'high' | 'expired' {
    const expDate = calculateExpirationDate(registrationDate);
    const timeRem = calculateTimeRemaining(expDate);

    if (timeRem.isExpired) return 'expired';
    if (timeRem.months < 1) return 'high';
    if (timeRem.months < 6) return 'medium';
    return 'low';
}

/**
 * Calculate time remaining until a specific end date
 */
export function calculateTimeRemainingToDate(endDate: string): {
    months: number;
    days: number;
    isExpired: boolean;
    text: string;
} {
    let expDate = new Date(endDate);
    if (isNaN(expDate.getTime())) {
        const [day, month, year] = endDate.split('/');
        expDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    const timeRem = calculateTimeRemaining(expDate);
    return {
        ...timeRem,
        text: formatTimeRemaining(timeRem.months, timeRem.days)
    };
}

/**
 * Get compliance status for an existing validity end date
 */
export function getValidityStatus(endDate: string): {
    className: string;
    text: string;
    bgColor: string;
    textColor: string;
} {
    const timeRemaining = calculateTimeRemainingToDate(endDate);
    return getComplianceStatus(timeRemaining);
}

/**
 * Get full validity display for existing end date
 */
export function getValidityDisplay(endDate: string): {
    endDate: string;
    timeRemaining: string;
    status: ReturnType<typeof getComplianceStatus>;
} {
    const timeRem = calculateTimeRemainingToDate(endDate);
    const status = getComplianceStatus(timeRem);
    const formattedEndDate = new Date(endDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    return {
        endDate: formattedEndDate,
        timeRemaining: formatTimeRemaining(timeRem.months, timeRem.days),
        status
    };
}

/**
 * Calculate GST filing delay details
 */
export function getGSTFilingDelay(rtntype: string, dof: string): {
    isLate: boolean;
    daysLate: number;
} {
    if (!dof) return { isLate: false, daysLate: 0 };

    // Parse dd-mm-yyyy format correctly
    const [day, month, year] = dof.split('-');
    const filingDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const filingDay = filingDate.getDate();

    let dueDay: number;

    // Set due dates based on return type
    switch (rtntype) {
        case 'GSTR1':
            dueDay = 11;
            break;
        case 'GSTR3B':
            dueDay = 21;
            break;
        default:
            return { isLate: false, daysLate: 0 }; // Unknown return type
    }

    // Calculate days late 
    const daysLate = Math.max(0, filingDay - dueDay);

    return {
        isLate: filingDay > dueDay,
        daysLate
    };
}

/**
 * Get GST filing status display
 */
export function getGSTFilingStatus(rtntype: string, dof: string, status: string): {
    text: string;
    className: string;
} {
    if (status !== 'Filed') {
        return {
            text: 'Not Filed',
            className: 'text-red-600'
        };
    }

    const delayInfo = getGSTFilingDelay(rtntype, dof);

    if (!delayInfo.isLate) {
        return {
            text: 'On Time',
            className: 'text-green-600'
        };
    }

    const dayText = delayInfo.daysLate === 1 ? 'day' : 'days';

    return {
        text: `${delayInfo.daysLate} ${dayText} late`,
        className: 'text-orange-600'
    };
}
